import { inject, injectable } from 'inversify'
import { action, observable, makeObservable, runInAction } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { HttpGateway } from '@core/services/http.gateway'
import type { ClockGateway } from '@core/services/clock.gateway'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type { OperationHandle, OperationOwner } from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'
import type { ActionFailurePm } from '@core/feedback/feedback.packing'
import { mapReadFailure } from '@core/services/gateway-failure.helpers'

// Long enough that a poll is not a meaningful chunk of traffic, short enough
// that a dot going green/grey reads as "current" rather than "stale". Half
// of the backend's PRESENCE_WINDOW_SECONDS (120s default), so a person
// actively in the app never flickers offline between polls.
const PRESENCE_POLL_INTERVAL_MS = 45_000

const PresenceDtoSchema = z.object({ emails: z.array(z.string()) })

// Same failure shape as VersionRepository's check — a hung poll's own
// timeout is the only failure that can occur; there is no user-facing state
// for a rejected/unparseable response, so those swallow to a no-op success.
type PresenceCheckFailure = ActionFailurePm

export const PRESENCE_CHECK_OPERATION = defineOperation<null, PresenceCheckFailure>({
  id: operationId('presence.check'),
  key: () => operationKey('current'),
  groups: () => [operationGroup('session'), operationGroup('presence')],
  pending: { kind: 'ignore' },
  safety: { kind: 'read' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

/**
 * Who, among the people visible to the current session, has used the app
 * recently — GET /presence, which already scopes its answer the same way
 * GET /people does (see backend/src/routes/authorization.helpers.ts). Polled
 * rather than pushed: no websocket/SSE infrastructure exists in this app,
 * and a 45s-stale dot is an acceptable approximation for "is this person
 * around right now."
 *
 * Same shape as VersionRepository: an AppLifecycle participant that starts
 * its own poll, guarded by `pollingStarted` so a second `initialize()` call
 * never registers a second timer.
 */
@injectable()
export class PresenceRepository implements AppLifecycle, UserScopedState {
  onlineEmailsPm: readonly string[] | null = null

  @inject(Types.IClockGateway)
  private clockGateway!: ClockGateway

  private readonly dataGateway: HttpGateway
  private readonly operationOwner: OperationOwner
  private readonly checkOperation: OperationHandle<null, PresenceCheckFailure>

  private pollingStarted = false

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
  ) {
    this.dataGateway = dataGateway
    this.operationOwner = operationRepository.createOwner({ id: 'presence.repository', lifetime: 'session' })
    this.checkOperation = this.operationOwner.register(PRESENCE_CHECK_OPERATION)

    makeObservable(this, {
      onlineEmailsPm: observable.ref,
      checkNow: action,
    })
  }

  initialize(): void {
    if (this.pollingStarted) return
    this.pollingStarted = true

    void this.checkNow()
    this.clockGateway.everyInterval(PRESENCE_POLL_INTERVAL_MS, () => {
      void this.checkNow()
    })
  }

  isOnline = (email: string): boolean => (this.onlineEmailsPm ?? []).includes(email)

  checkNow = async (): Promise<void> => {
    await this.checkOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.get('/presence', { signal, idempotencyKey: null })
        if (!response.success) return operationFailed(mapReadFailure(response))

        const parsed = PresenceDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'invalid-external-data', retryable: false })

        return operationSucceeded(Object.freeze([...parsed.data.emails]))
      },
      {
        succeeded: (emails) => {
          this.onlineEmailsPm = emails
        },
        // A failed/timed-out check just means "couldn't tell, stay quiet" —
        // the next scheduled tick is the next attempt, same as VersionRepository.
        failed: null,
      },
    )
  }

  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.onlineEmailsPm = null
    })
  }
}
